import { useEffect, useState, useCallback } from "react";
import {
  X,
  ArrowLeft,
  Brain,
  Clock,
  AlertTriangle,
  CircleDashed,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useMspTenantAlerts, useMspBulkUpdateTenantAlerts } from "@/hooks/use-api";
import {
  severityVariant,
  formatRelativeTime,
  formatAbsoluteTime,
  statusBgColors,
} from "@/lib/formatters";
import { cn } from "@/lib/utils";
import type { Alert, AlertStatus, MspTenant } from "@/types/api";

const PAGE_LIMIT = 25;

interface TenantDrillDownProps {
  tenant: MspTenant | null;
  onClose: () => void;
}

export function TenantDrillDown({ tenant, onClose }: TenantDrillDownProps) {
  const [offset, setOffset] = useState(0);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [prevTenantId, setPrevTenantId] = useState(tenant?.id);

  // New tenant → reset during render (before the query reads `offset`), so the
  // fetch never fires with the previous tenant's offset and no stale detail
  // renders under the new header. React discards this render pass. Clearing the
  // selection here also stops one child's selected ids leaking into a bulk
  // action on another child (#775).
  if (tenant?.id !== prevTenantId) {
    setPrevTenantId(tenant?.id);
    setOffset(0);
    setSelectedAlert(null);
    setSelectedIds(new Set());
    setBulkError(null);
  }

  const { data, isLoading } = useMspTenantAlerts(tenant?.id ?? null, {
    limit: PAGE_LIMIT,
    offset,
  });
  const bulkUpdate = useMspBulkUpdateTenantAlerts(tenant?.id ?? null);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!tenant) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        // Escape backs out of the detail first, then closes the panel.
        if (selectedAlert) setSelectedAlert(null);
        else onClose();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [tenant, onClose, selectedAlert]);

  if (!tenant) return null;

  const alerts = data?.alerts ?? [];
  const hasNext = alerts.length === PAGE_LIMIT;
  const hasPrev = offset > 0;
  const allSelected = alerts.length > 0 && alerts.every((a) => selectedIds.has(a.id));

  function selectAll() {
    setSelectedIds(allSelected ? new Set() : new Set(alerts.map((a) => a.id)));
  }

  function handleBulkAction(status: AlertStatus) {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkError(null);
    bulkUpdate.mutate(
      { alertIds: ids, status },
      {
        onSuccess: () => setSelectedIds(new Set()),
        onError: (err) => setBulkError(err.message),
      },
    );
  }

  // Changing page closes the detail (its alert belongs to the prior page) and
  // clears the selection — off-page checkboxes aren't visible, so a bulk action
  // must not silently act on ids the analyst can no longer see.
  function goToPage(next: number) {
    setSelectedAlert(null);
    setSelectedIds(new Set());
    setBulkError(null);
    setOffset(Math.max(0, next));
  }

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${tenant.name} alerts`}
        className="fixed right-0 top-0 z-50 flex h-full w-[560px] max-w-full flex-col border-l bg-background shadow-xl animate-in slide-in-from-right"
      >
        <div className="flex items-center justify-between border-b p-4">
          <div className="flex items-center gap-2">
            {selectedAlert && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Back to alerts"
                onClick={() => setSelectedAlert(null)}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div>
              <h2 className="text-lg font-semibold">{tenant.name}</h2>
              <p className="text-sm text-muted-foreground">
                {selectedAlert ? "Alert detail" : "Recent alerts for this tenant"}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" aria-label="Close" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {selectedAlert ? (
          <AlertDetail alert={selectedAlert} />
        ) : (
          <>
            {/* Bulk triage bar — ack/resolve this child's alerts from the MSP
                pane (#775). Bulk with a single id also covers per-row triage. */}
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-3 border-b bg-muted/50 px-4 py-2">
                <span className="text-sm font-medium">
                  {selectedIds.size} selected
                </span>
                <div className="ml-auto flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkAction("acknowledged")}
                    disabled={bulkUpdate.isPending}
                  >
                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                    Acknowledge
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkAction("resolved")}
                    disabled={bulkUpdate.isPending}
                  >
                    <XCircle className="mr-1.5 h-3.5 w-3.5" />
                    Resolve
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}
            {bulkError && (
              <div className="border-b border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-400">
                {bulkError}
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="space-y-2 p-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : alerts.length === 0 ? (
                <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
                  {hasPrev ? "No more alerts" : "No alerts for this tenant"}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          aria-label="Select all alerts"
                          checked={allSelected}
                          onChange={selectAll}
                          className="h-4 w-4 rounded border-input accent-primary"
                        />
                      </TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead className="text-right">Last Seen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.map((alert) => (
                      <TableRow
                        key={alert.id}
                        tabIndex={0}
                        onClick={() => setSelectedAlert(alert)}
                        onKeyDown={(e) => {
                          // Only the row itself opens the detail — keys from a
                          // focused child (the select checkbox) must not bubble
                          // up and open the detail while toggling selection.
                          if (e.target !== e.currentTarget) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedAlert(alert);
                          }
                        }}
                        data-state={selectedIds.has(alert.id) ? "selected" : undefined}
                        className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                      >
                        <TableCell
                          className="w-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            aria-label={`Select alert: ${alert.title}`}
                            checked={selectedIds.has(alert.id)}
                            onChange={() => toggleSelect(alert.id)}
                            className="h-4 w-4 rounded border-input accent-primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Badge variant={severityVariant(alert.severity)}>
                            {alert.severity}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[250px] truncate font-medium">
                          {alert.title}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {formatRelativeTime(alert.last_seen_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {!isLoading && (alerts.length > 0 || hasPrev) && (
              <div className="flex items-center justify-between border-t p-3">
                <span className="text-xs text-muted-foreground">
                  {alerts.length > 0
                    ? `Showing ${offset + 1}–${offset + alerts.length}`
                    : "No results on this page"}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(offset - PAGE_LIMIT)}
                    disabled={!hasPrev}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(offset + PAGE_LIMIT)}
                    disabled={!hasNext}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

const LEGACY_AI_CONTEXT_KEYS = ["ai_summary", "investigation", "enrichment"];

/**
 * Read-only alert detail for the MSP drill-down. Rendered from the alert object
 * already in the list response (no fetch). Triage (acknowledge/resolve, single
 * or bulk) is done from the list via checkbox selection (#775); this detail view
 * deliberately stays read-only and carries no feedback/mute/retry actions.
 */
function AlertDetail({ alert }: { alert: Alert }) {
  const [eventsExpanded, setEventsExpanded] = useState(false);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="flex items-center gap-2 border-b p-4">
        <Badge variant={severityVariant(alert.severity)}>{alert.severity}</Badge>
        <h3 className="text-base font-semibold leading-tight">{alert.title}</h3>
      </div>

      <div className="flex items-center gap-2 border-b px-4 py-3">
        <span
          className={cn(
            "rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize",
            statusBgColors[alert.status] ?? "bg-muted text-muted-foreground",
          )}
        >
          {alert.status}
        </span>
      </div>

      <div className="space-y-4 p-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-muted-foreground">Source Type</p>
            <p className="font-medium">{alert.source_type || "---"}</p>
          </div>
          <div>
            <p className="text-muted-foreground">First Seen</p>
            <p className="font-medium">{formatAbsoluteTime(alert.first_seen_at)}</p>
            <p className="text-xs text-muted-foreground">
              {formatRelativeTime(alert.first_seen_at)}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Last Seen</p>
            <p className="font-medium">{formatAbsoluteTime(alert.last_seen_at)}</p>
            <p className="text-xs text-muted-foreground">
              {formatRelativeTime(alert.last_seen_at)}
            </p>
          </div>
        </div>

        {alert.description && (
          <div>
            <h4 className="mb-1 text-sm font-medium text-muted-foreground">
              Description
            </h4>
            <p className="text-sm">{alert.description}</p>
          </div>
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Brain className="h-4 w-4 text-primary" />
              AI Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            {alert.enrichment_status === "pending" && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4 shrink-0" />
                <span>Analysis pending</span>
              </div>
            )}
            {!alert.enrichment_status && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CircleDashed className="h-4 w-4 shrink-0" />
                <span>No AI analysis — unavailable or not started</span>
              </div>
            )}
            {alert.enrichment_status === "failed" && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>Analysis unavailable — manual review required</span>
              </div>
            )}
            {alert.enrichment_status === "complete" && alert.ai_context && (
              <div className="space-y-3 text-sm">
                <div>
                  <p className="mb-1 font-medium">Summary</p>
                  <p className="whitespace-pre-wrap text-muted-foreground">
                    {alert.ai_context.summary}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-medium">Severity Reasoning</p>
                  <p className="whitespace-pre-wrap text-muted-foreground">
                    {alert.ai_context.severity_reasoning}
                  </p>
                </div>
                {alert.ai_context.recommended_actions.length > 0 && (
                  <div>
                    <p className="mb-1 font-medium">Recommended Actions</p>
                    <ul className="list-inside list-disc space-y-1 text-muted-foreground">
                      {alert.ai_context.recommended_actions.map((action, i) => (
                        <li key={i}>{action}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {alert.ai_context.mitre_technique && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">MITRE Technique</span>
                    <Badge variant="outline" className="font-mono text-xs">
                      {alert.ai_context.mitre_technique}
                    </Badge>
                  </div>
                )}
              </div>
            )}
            {alert.enrichment_status === "complete" && !alert.ai_context && (
              <p className="text-sm text-muted-foreground">
                Analysis completed but no data available
              </p>
            )}
          </CardContent>
        </Card>

        {alert.context &&
          Object.keys(alert.context).filter(
            (k) => !LEGACY_AI_CONTEXT_KEYS.includes(k),
          ).length > 0 && (
            <div>
              <h4 className="mb-2 text-sm font-medium text-muted-foreground">
                Rule Context
              </h4>
              <div className="space-y-1 text-sm">
                {Object.entries(alert.context)
                  .filter(([key]) => !LEGACY_AI_CONTEXT_KEYS.includes(key))
                  .map(([key, value]) => (
                    <div key={key} className="flex justify-between gap-2">
                      <span className="text-muted-foreground">
                        {key.replace(/_/g, " ")}
                      </span>
                      <span className="text-right font-medium">
                        {typeof value === "string"
                          ? value
                          : JSON.stringify(value)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

        {alert.entities && Object.keys(alert.entities).length > 0 && (
          <div>
            <h4 className="mb-2 text-sm font-medium text-muted-foreground">
              Entities
            </h4>
            <div className="space-y-2">
              {Object.entries(alert.entities).map(([group, values]) => (
                <div key={group}>
                  <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                    {group}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {values.map((val) => (
                      <Badge
                        key={val}
                        variant="secondary"
                        className="font-mono text-xs"
                      >
                        {val}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {alert.matched_events && alert.matched_events.length > 0 && (
          <div>
            <button
              onClick={() => setEventsExpanded(!eventsExpanded)}
              className="flex w-full items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              {eventsExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              Matched Events ({alert.matched_events.length})
            </button>
            {eventsExpanded && (
              <pre className="mt-2 max-h-96 overflow-auto rounded bg-muted p-3 text-xs">
                {alert.matched_events.length > 50
                  ? JSON.stringify(alert.matched_events.slice(0, 50), null, 2) +
                    `\n\n... and ${alert.matched_events.length - 50} more events`
                  : JSON.stringify(alert.matched_events, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
